import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import authApi from "../api/authApi";
import GoogleAuthButton from "../components/auth/GoogleAuthButton";

const getAuthErrorMessage = (err, fallback) => {
  const data = err?.data || err?.response?.data;
  if (data?.message) return data.message;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) return data.errors[0].msg;
  return fallback;
};

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Please enter your full name";
    }
    
    if (!formData.username || formData.username.length < 8) {
      newErrors.username = "Username must be at least 8 characters";
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    
    if (!validate()) return;
    
    setIsLoading(true);
    
    try {
      const response = await authApi.signup({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        name: formData.name,
        website: formData.website,
        expirydate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
      });

      if (response.errors) {
        // Handle backend validation errors
        const backendErrors = {};
        response.errors.forEach(err => {
          backendErrors[err.param] = err.msg;
        });
        setErrors(backendErrors);
        return;
      }

      navigate("/login", {
        state: {
          message:
            response.message ||
            "Signup successful! Please check your email to verify your account.",
        },
      });
      
    } catch (err) {
      const data = err?.data || err?.response?.data;
      if (Array.isArray(data?.errors)) {
        const backendErrors = {};
        data.errors.forEach((error) => {
          backendErrors[error.param] = error.msg;
        });
        setErrors(backendErrors);
      }
      setApiError(getAuthErrorMessage(err, "Signup failed. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(
    async (credential) => {
      setApiError(null);
      setErrors({});
      setIsLoading(true);

      try {
        const data = await authApi.googleLogin({ credential });
        localStorage.setItem("token", data.token);
        navigate("/app/dashboard", {
          state: { message: data.message },
        });
      } catch (err) {
        setApiError(
          getAuthErrorMessage(err, "Google sign up failed. Please try again.")
        );
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  const baseUrl = "https://www.caloriespal.com";
  const pageTitle = "Create a CaloriesPal Account | Sign up for smarter nutrition labeling";
  const pageDescription =
    "Sign up for CaloriesPal to design compliant nutrition labels, manage recipes, and access our ingredient intelligence suite.";
  const registerSchema = {
    "@context": "https://schema.org",
    "@type": "RegisterAction",
    name: "Create a CaloriesPal account",
    target: `${baseUrl}/signup`,
    result: {
      "@type": "Person",
      description: "Registered CaloriesPal creator",
    },
    potentialAction: {
      "@type": "ConfirmAction",
      name: "Verify account",
    },
  };
  const features = [
    "Auto-compliant nutrition labels",
    "Cost tracking & ingredient insights",
    "Email verification & secure access",
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <meta
          name="keywords"
          content="nutrition label signup, caloriespal account, recipe labeling software, food tech signup"
        />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`${baseUrl}/signup`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${baseUrl}/assets/images/Calories (1).png`} />
        <meta property="og:site_name" content="CaloriesPal" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${baseUrl}/assets/images/Calories (1).png`} />
        <link rel="canonical" href={`${baseUrl}/signup`} />
        <script type="application/ld+json">{JSON.stringify(registerSchema)}</script>
      </Helmet>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, md: 4 },
          py: { xs: 6, md: 10 },
          background: "radial-gradient(circle at top, #d9f99d 0%, #0f172a 55%)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 8 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              color: "white",
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              backdropFilter: "blur(20px)",
              background: "rgba(15, 23, 42, 0.65)",
              boxShadow: "0 25px 60px rgba(15, 23, 42, 0.4)",
            }}
          >
            <Chip
              label="Create your nutrition HQ"
              color="success"
              sx={{ mb: 2, fontWeight: 600 }}
            />
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
              Sign up in under a minute.
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 4, fontSize: "1.1rem" }}>
              Ship compliant labels faster, keep every recipe synced with your teams, and unlock
              our ingredient intelligence platform—starting with your free trial.
            </Typography>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />
            <Stack spacing={3}>
              {features.map((feature) => (
                <Box
                  key={feature}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "rgba(132,204,22,0.2)",
                      border: "1px solid rgba(132,204,22,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#d9f99d",
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </Box>
                  <Typography sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.9)" }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: "white",
              borderRadius: 4,
              p: { xs: 3, md: 4 },
              boxShadow: "0 20px 45px rgba(15,23,42,0.15)",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Button
              size="small"
              onClick={() => navigate("/")}
              sx={{ alignSelf: "flex-start", mb: 2, textTransform: "none" }}
            >
              ← Back to Home
            </Button>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Create your CaloriesPal account
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
              Access the full recipe + labeling suite with bank-grade security and email
              verification.
            </Typography>

            {apiError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {apiError}
              </Alert>
            )}

            <GoogleAuthButton
              label="signup_with"
              onCredential={handleGoogleCredential}
              disabled={isLoading}
            />

            <Divider sx={{ my: 2 }}>or create with email</Divider>

            <TextField
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              margin="dense"
              required
            />

            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username || "Use at least 8 characters"}
              fullWidth
              margin="dense"
              required
            />

            <TextField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              margin="dense"
              required
            />

            <TextField
              label="Website (optional)"
              name="website"
              value={formData.website}
              onChange={handleChange}
              fullWidth
              margin="dense"
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
              <TextField
                label="Password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password || "At least 8 characters"}
                fullWidth
                required
              />
              <TextField
                label="Confirm Password"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                fullWidth
                required
              />
            </Stack>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: "1rem",
                textTransform: "none",
                background: "linear-gradient(135deg,#84cc16,#22d3ee)",
                boxShadow: "0 15px 30px rgba(34,211,238,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg,#a3e635,#2dd4bf)",
                },
              }}
            >
              {isLoading ? "Creating Account..." : "Create free account"}
            </Button>

            <Typography variant="caption" sx={{ color: "text.secondary", mt: 1 }}>
              By signing up you agree to the CaloriesPal Terms of Service and Privacy Policy.
            </Typography>

            <Divider sx={{ my: 2 }}>Already registered?</Divider>

            <Typography variant="body2" align="center">
              Already have an account?{" "}
              <Button onClick={() => navigate("/login")} size="small">
                Log in
              </Button>
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="center"
              sx={{ mt: 1 }}
            >
              <Button
                size="small"
                onClick={() => navigate("/")}
                sx={{ textTransform: "none" }}
              >
                Back to Home
              </Button>
              <Button
                size="small"
                onClick={() => navigate("/foods")}
                sx={{ textTransform: "none" }}
              >
                Browse Foods
              </Button>
              <Button
                size="small"
                onClick={() => navigate("/login")}
                sx={{ textTransform: "none" }}
              >
                Sign In
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Signup;
